import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { message } from "antd";
import { fetchQuizAttemptMeta, fetchQuizAttemptDetails } from "../../../service/UserService/quizAttemptDetailsService";

export const tryParseJSON = (value) => {
  if (!value || value === "null") return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed;
    if (typeof parsed === "string") return [parsed];
    return [JSON.stringify(parsed)];
  } catch {
    return [value];
  }
};

export const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });

export const useQuizAttemptDetails = (setHideSidebar) => {
  const [searchParams] = useSearchParams();

  const userId   = searchParams.get("userId");
  const courseId = searchParams.get("courseId");

  // Meta state
  const [meta, setMeta] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);

  // Selected attempt state
  const [selectedAttemptIndex, setSelectedAttemptIndex] = useState(0);
  const [selectedAttemptDetail, setSelectedAttemptDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    setHideSidebar?.(true);
    return () => setHideSidebar?.(false);
  }, []);

  // Step 1: load meta on mount
  useEffect(() => {
    if (!userId || !courseId) return;
    const load = async () => {
      setMetaLoading(true);
      try {
        const data = await fetchQuizAttemptMeta(userId, courseId);
        setMeta(data);
      } catch (err) {
        message.error(err.message);
      } finally {
        setMetaLoading(false);
      }
    };
    load();
  }, [userId, courseId]);

  // Step 2: load attempt details when selected attempt changes
  useEffect(() => {
    if (!meta?.attempts?.length) return;
    const attempt = meta.attempts[selectedAttemptIndex];
    if (!attempt?.attemptId) return;

    const load = async () => {
      setDetailLoading(true);
      try {
        const data = await fetchQuizAttemptDetails(userId, courseId, attempt.attemptId);
        setSelectedAttemptDetail(data);
      } catch (err) {
        message.error(err.message);
      } finally {
        setDetailLoading(false);
      }
    };
    load();
  }, [meta, selectedAttemptIndex, userId, courseId]);

  // Derived values from new response structure
  const score = selectedAttemptDetail ? {
    totalScore:        selectedAttemptDetail.totalScore,
    maxPossibleScore:  selectedAttemptDetail.maxPossibleScore,
    percentageScore:   selectedAttemptDetail.percentageScore,
    correctAnswers:    selectedAttemptDetail.correctAnswers,
    totalQuestions:    selectedAttemptDetail.totalQuestions,
    submittedAt:       selectedAttemptDetail.finishedAt,
  } : null;

  const passed         = selectedAttemptDetail?.isPassed ?? false;
  const submissionType = selectedAttemptDetail?.submissionType || "MANUAL_SUBMIT";
  const passingMarks   = selectedAttemptDetail?.passingScore ?? null;

  // Time taken from startedAt and finishedAt
  const timeTaken = (() => {
    if (!selectedAttemptDetail?.startedAt || !selectedAttemptDetail?.finishedAt) return null;
    const diff = Math.floor(
      (new Date(selectedAttemptDetail.finishedAt) - new Date(selectedAttemptDetail.startedAt)) / 1000
    );
    const m = Math.floor(diff / 60);
    const s = diff % 60;
    return `${m}m ${s}s`;
  })();

  return {
    // User + course info now from meta
    quizName:   meta?.quizTitle   || "Quiz",
    userName:     meta?.fullName      || "N/A",
    userRole:     searchParams.get("userRole") || "",
    userAvatar:   searchParams.get("userAvatar") || null,

    // Attempts list from meta
    attempts: meta?.attempts || [],

    // Selection
    selectedAttemptIndex,
    setSelectedAttemptIndex,

    // Detail data
    selectedAttempt: selectedAttemptDetail,
    score,
    passed,
    timeTaken,
    submissionType,
    passingMarks,

    // Loading — show full loader only on meta, detail loader is separate
    loading: metaLoading || detailLoading,
  };
};